import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft } from "lucide-react";

/**
 * Shared shell for editorial blog posts.
 * Drop-cap on first paragraph, generous spacing, headings styled in serif.
 */
export const BlogShell = ({ category, title, author, date, hero, children }) => (
  <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2]">
    <div className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
      <div className="flex items-center justify-between mb-16">
        <Link to="/" className="flex items-center gap-2" data-testid="blog-logo-link">
          <Sparkles className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />
          <span className="font-serif text-2xl">StyleMe</span>
        </Link>
        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2]"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> All posts
        </Link>
      </div>

      {category && (
        <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">{category}</span>
      )}
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 font-light leading-[1.05]">
        {title}
      </h1>
      <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#A8A6A1]">
        <span>{author}</span>
        <span className="w-1 h-1 rounded-full bg-[#A8A6A1]" />
        <span>{date}</span>
      </div>

      {hero && (
        <div className="mt-12 aspect-[16/9] overflow-hidden rounded-sm bg-[#1A1820]">
          <img src={hero} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      <article
        data-testid="blog-article"
        className={
          "mt-16 space-y-7 text-[16.5px] leading-[1.85] text-[#D6D3CE] " +
          "[&_h2]:font-serif [&_h2]:text-2xl sm:[&_h2]:text-3xl [&_h2]:text-[#F7F5F2] [&_h2]:mt-16 [&_h2]:mb-6 [&_h2]:font-light [&_h2]:leading-tight " +
          "[&_h3]:text-xs [&_h3]:uppercase [&_h3]:tracking-[0.3em] [&_h3]:text-[#E3C2B0] [&_h3]:mt-10 [&_h3]:mb-3 [&_h3]:font-medium " +
          "[&_a]:text-[#E3C2B0] [&_a:hover]:text-[#F0D4C5] [&_a]:underline [&_a]:underline-offset-4 " +
          "[&_ul]:space-y-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-[#D6D3CE] " +
          "[&_strong]:text-[#F7F5F2] [&_strong]:font-medium " +
          "[&_em]:text-[#E3C2B0] [&_em]:not-italic [&_em]:font-medium " +
          "[&_blockquote]:border-l-2 [&_blockquote]:border-[#E3C2B0] [&_blockquote]:pl-6 [&_blockquote]:my-8 [&_blockquote]:italic [&_blockquote]:text-[#F7F5F2] [&_blockquote]:text-lg [&_blockquote]:leading-relaxed " +
          "[&>p:first-of-type]:first-letter:font-serif [&>p:first-of-type]:first-letter:text-5xl [&>p:first-of-type]:first-letter:float-left [&>p:first-of-type]:first-letter:mr-3 [&>p:first-of-type]:first-letter:leading-none [&>p:first-of-type]:first-letter:text-[#E3C2B0] [&>p:first-of-type]:first-letter:mt-1"
        }
      >
        {children}
      </article>

      <div className="mt-24 pt-10 border-t border-white/10">
        <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">From StyleMe</span>
        <h3 className="font-serif text-3xl mt-3 font-light">Get your own AI styling.</h3>
        <p className="mt-3 text-[#A8A6A1] leading-relaxed">
          Upload your portrait, pick the occasion, and get a complete AI-styled look —
          outfit, makeup, hairstyle, accessories. With shoppable products from Amazon India.
        </p>
        <Link
          to="/auth"
          data-testid="blog-cta"
          className="mt-6 inline-flex items-center gap-3 bg-[#E3C2B0] hover:bg-[#F0D4C5] text-[#0A0908] px-8 py-3 rounded-sm text-xs uppercase tracking-[0.3em] transition-colors"
        >
          Try StyleMe
        </Link>
      </div>
    </div>
  </div>
);

export default BlogShell;
